import React, { useState, useRef, useEffect, useMemo } from 'react';
import logoImg from '../../assets/images/Logo.png';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  HiOutlineChartPie,
  HiOutlineUsers,
  HiOutlineTruck,
  HiOutlineCube,
  HiOutlineDocumentText,
  HiOutlineCog,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineSearch,
  HiOutlineClipboardList,
  HiOutlineShoppingCart,
  HiOutlineSparkles,
  HiOutlineMap,
  HiOutlineShieldCheck,
} from 'react-icons/hi';
import { inventoryService } from '../../api/inventoryService';
import { mailboxService } from '../../api/mailboxService';
import taskService from '../../api/taskService';
import { useAuth } from '../../context/AuthContext';
import { filterMenuItemsByPermission } from '../../utils/rbacNavigation';

export const buildMenuItems = ({ agriExportEnabled, batchTrackingEnabled, itemVariantsEnabled, projectManagementEnabled, manufacturingEnabled, qaiReportGeneratorEnabled, qaiDashboardGeneratorEnabled, deliveryManagementEnabled, crmEnabled, crmMailboxEnabled, posEnabled, expressMarketEnabled, creditControlEnabled, procurementEnabled, inboxUnreadCount = 0, todoCount = 0 }) => [
  // ── Front-office ──────────────────────────────────────────────
  {
    label: 'Dashboard',
    path: '/',
    icon: HiOutlineChartPie,
  },
  {
    label: 'My To-Do',
    path: '/tasks',
    icon: HiOutlineClipboardList,
    badge: todoCount || null,
  },
  ...(crmEnabled
    ? [{
        label: 'CRM',
        icon: HiOutlineUsers,
        children: [
          { label: 'Dashboard', path: '/crm/dashboard' },
          { label: 'Customer 360', path: '/crm/customer-360' },
          ...(crmMailboxEnabled ? [{ label: 'My Inbox', path: '/crm/mailbox', badge: inboxUnreadCount || null }] : []),
          { label: 'Contacts', path: '/crm/contacts' },
          { label: 'Opportunities', path: '/crm/opportunities' },
          { label: 'Sales Pipeline', path: '/crm/pipeline' },
          { label: 'Activities', path: '/crm/activities' },
          { label: 'Workflow Rules', path: '/crm/workflows' },
          { label: 'Lead Discovery', path: '/crm/discovery/templates' },
          { label: 'Lead Review Queue', path: '/crm/discovery/queue' },
          { label: 'Pipeline Settings', path: '/crm/pipeline-settings' },
          { label: 'Opt-outs', path: '/crm/opt-outs' },
        ],
      }]
    : []),
  {
    label: 'Customers',
    icon: HiOutlineUsers,
    children: [
      { label: 'Listing', path: '/customers' },
      { label: 'Groups', path: '/customers/groups' },
      { label: 'Cities', path: '/customers/cities' },
      { label: 'Areas', path: '/customers/areas' },
      { label: 'Sales Reps', path: '/customers/sales-reps' },
      { label: 'Receive Payments', path: '/customers/receipts' },
      { label: 'Adjustments', path: '/customers/adjustments' },
      { label: 'Volume Discounts', path: '/customers/discounts' },
      { label: 'Joint Business Plans', path: '/customers/jbp', permission: 'customers.jbp.view' },
      {
        label: 'Reports',
        children: [
          { label: 'Transactions Enquiry', path: '/customers/reports/transactions' },
          { label: 'Age Analysis', path: '/customers/reports/aging' },
          { label: 'Sales Rep Commission', path: '/customers/reports/sales-rep-commission' },
          { label: 'Sales Rep Report', path: '/customers/reports/sales-rep' },
          { label: 'Volume Discount Analysis', path: '/customers/reports/volume-discount-analysis' },
          { label: 'Statements', path: '/customers/reports/statements' },
          { label: 'Balance Reconciliation', path: '/customers/reports/balance-reconciliation' },
        ],
      },
    ],
  },
  // ── Transactions ──────────────────────────────────────────────
  {
    label: 'Order Entry',
    icon: HiOutlineDocumentText,
    children: [
      { label: 'Quote & Sales Order', path: '/orders/quotes-sales-orders' },
      { label: 'Sales Invoice', path: '/orders/invoices' },
      { label: 'Credit Notes', path: '/orders/credit-notes' },
      { label: 'Purchase Orders', path: '/orders/purchase-orders' },
      { label: 'Return to Supplier', path: '/orders/rts' },
      {
        label: 'Reports',
        children: [
          { label: 'Sales Order', path: '/orders/reports/sales-order' },
          { label: 'Credit Notes', path: '/orders/reports/credit-notes' },
          { label: 'Purchase Order', path: '/orders/reports/purchase-order' },
          { label: 'GRV / Receipt Report', path: '/orders/reports/grv' },
          { label: 'Supplier Invoice', path: '/orders/reports/supplier-invoice' },
          { label: 'Return To Supplier', path: '/orders/reports/return-to-supplier' },
          { label: 'Delivery Schedule and Priority', path: '/orders/reports/delivery-schedule' },
          { label: 'Open Orders Aging', path: '/orders/reports/open-orders-aging' },
          { label: 'Backorder and Fill Rate', path: '/orders/reports/backorder-fill-rate' },
          { label: 'Order to Invoice Cycle Time', path: '/orders/reports/order-to-invoice-cycle' },
          { label: 'Cancellation and Return Analysis', path: '/orders/reports/cancellation-return-analysis' },
          { label: 'Gross Margin by Order', path: '/orders/reports/gross-margin-by-order' },
          { label: 'PO vs GRV Variance', path: '/orders/reports/po-grv-variance' },
          { label: 'Customer and Supplier On-Time Performance', path: '/orders/reports/on-time-performance' },
        ],
      },
    ],
  },
  ...(expressMarketEnabled
    ? [{
        label: 'Express Market',
        path: '/express-market',
        icon: HiOutlineShoppingCart,
      }]
    : []),
  ...(posEnabled
    ? [{
        label: 'POS',
        icon: HiOutlineDocumentText,
        children: [
          { label: 'Till Monitor', path: '/pos' },
          { label: 'HQ Dashboard', path: '/pos/hq-dashboard', permission: 'nav.pos.hq_dashboard' },
          { label: 'Checkout', path: '/pos/checkout' },
          { label: 'Sessions', path: '/pos/sessions' },
          { label: 'Setup', path: '/pos/setup' },
          {
            label: 'Reports',
            children: [
              { label: 'Sales by Terminal & Payment Type', path: '/pos/reports/sales-by-terminal-payment' },
              { label: 'POS Inventory Sales', path: '/pos/reports/inventory-sales' },
              { label: 'Z / X Report', path: '/pos/reports/z-report' },
            ],
          },
        ],
      }]
    : []),
  ...(deliveryManagementEnabled
    ? [{
        label: 'Delivery',
        icon: HiOutlineMap,
        children: [
          { label: 'Manifests', path: '/delivery/manifests' },
          { label: 'Return Queue', path: '/delivery/return-queue' },
          { label: 'Drivers', path: '/delivery/drivers' },
          { label: 'Vehicles', path: '/delivery/vehicles' },
          { label: 'Zones', path: '/delivery/zones' },
          { label: 'Return Reasons', path: '/delivery/return-reasons' },
          {
            label: 'Reports',
            children: [
              { label: 'Delivery Efficiency', path: '/delivery/reports/efficiency' },
              { label: 'Manifest Throughput', path: '/delivery/reports/manifest-throughput' },
              { label: 'Driver Performance', path: '/delivery/reports/driver-performance' },
              { label: 'Return Analytics', path: '/delivery/reports/return-analytics' },
              { label: 'SLA Breach', path: '/delivery/reports/sla-breach' },
              { label: 'Zone Utilization & Load', path: '/delivery/reports/zone-utilization' },
              { label: 'Reissue Cycle', path: '/delivery/reports/reissue-cycle' },
            ],
          },
        ],
      }]
    : []),
  // My Deliveries sits OUTSIDE the Delivery group, immediately after it, and
  // that placement is forced rather than chosen.
  //
  // A driver must not hold nav.delivery: that single key gates routes/delivery.js,
  // where GET /manifests/:id returns oe_sales_invoices.grand_total for every stop.
  // But the Delivery group's own permission resolves to nav.delivery, and
  // filterMenuItemsByPermission drops a parent BEFORE it looks at any child — so
  // nesting this leaf inside the group would hide it from exactly the people it
  // exists for. Top-level, a Delivery Rider/Driver sees this one entry and
  // nothing else, while the office sees it directly under their Delivery block.
  ...(deliveryManagementEnabled
    ? [{
        label: 'My Deliveries',
        icon: HiOutlineTruck,
        path: '/my-deliveries',
        permission: 'nav.my_deliveries',
      }]
    : []),
  // ── Supply & Operations ───────────────────────────────────────
  ...(procurementEnabled
    ? [{
        label: 'Procurement',
        icon: HiOutlineClipboardList,
        children: [
          { label: 'Requisitions', path: '/procurement/requisitions' },
          { label: 'RFQs', path: '/procurement/rfqs' },
          { label: 'PO Approvals', path: '/procurement/po-approvals' },
          { label: 'Supplier Scorecard', path: '/procurement/supplier-scorecard' },
          { label: 'Settings', path: '/procurement/settings' },
        ],
      }]
    : []),
  {
    label: 'Suppliers',
    icon: HiOutlineTruck,
    children: [
      { label: 'Listing', path: '/suppliers' },
      { label: 'Groups', path: '/suppliers/groups' },
      { label: 'Make Payments', path: '/suppliers/payments' },
      { label: 'Adjustments', path: '/suppliers/adjustments' },
      { label: 'Payment Run', path: '/suppliers/reports/payment-run' },
      {
        label: 'Reports',
        children: [
          { label: 'Transactions Enquiry', path: '/suppliers/reports/transactions' },
          { label: 'Age Analysis', path: '/suppliers/reports/aging' },
          { label: 'Statements', path: '/suppliers/reports/statements' },
          { label: 'Balance Reconciliation', path: '/suppliers/reports/balance-reconciliation' },
        ],
      },
    ],
  },
  {
    label: 'Inventory',
    icon: HiOutlineCube,
    children: [
      { label: 'Items', path: '/inventory/items' },
      { label: 'Item Groups', path: '/inventory/groups', permission: 'inventory.groups.view' },
      { label: 'Units of Measure', path: '/inventory/uom' },
      { label: 'Price Lists', path: '/inventory/price-lists', permission: 'inventory.price_lists.view' },
      { label: 'Import Price Lists', path: '/inventory/import-price-lists', permission: 'inventory.price_lists.import_batch.manage' },
      { label: 'Locations', path: '/inventory/locations' },
      { label: 'Warehouses', path: '/inventory/warehouses' },
      { label: 'Stock Transfers', path: '/inventory/transfers' },
      { label: 'In-Transit Transfer (Issue)', path: '/inventory/intransit-transfers/issue', permission: 'inventory.intransit.send' },
      { label: 'In-Transit Transfer (Receive)', path: '/inventory/intransit-transfers/receive', permission: 'inventory.intransit.receive' },
      { label: 'Stock Adjustments', path: '/inventory/adjustments', permission: 'inventory.adjustments.create' },
      { label: 'Count', path: '/inventory/counts' },
      ...(batchTrackingEnabled ? [
        { label: 'Batch Tracking', path: '/inventory/batches' },
        { label: 'Trace Explorer', path: '/inventory/trace' },
      ] : []),
      ...(itemVariantsEnabled ? [{ label: 'Variant Attributes', path: '/inventory/variant-attributes', permission: 'inventory.variants.view' }] : []),
      { label: 'AI Reorder Suggestions', path: '/inventory/reorder-suggestions' },
      {
        label: 'Reports',
        children: [
          { label: 'Transaction Enquiry', path: '/inventory/reports/transactions' },
          { label: 'Warehouse Stock', path: '/inventory/reports/stock' },
          { label: 'Net Sales', path: '/inventory/reports/net-sales' },
          { label: 'Sales vs Purchases', path: '/inventory/reports/sales-vs-purchases' },
          { label: 'Gross Margin', path: '/inventory/reports/gross-margin' },
          { label: 'Movement Analysis', path: '/inventory/reports/movement-analysis' },
          { label: 'Inventory Turnover', path: '/inventory/reports/turnover' },
          { label: 'Supplier Performance', path: '/inventory/reports/supplier-performance' },
          { label: 'Pricing', path: '/inventory/reports/pricing' },
          { label: 'Costing', path: '/inventory/reports/costing' },
          { label: 'Stock Aging', path: '/inventory/reports/stock-aging' },
          { label: 'FIFO Costing', path: '/inventory/reports/fifo-costing' },
          { label: 'Valuation', path: '/inventory/reports/valuation' },
          { label: 'Count Variance', path: '/inventory/reports/count-variance' },
          ...(batchTrackingEnabled ? [
            { label: 'Lot Stock on Hand', path: '/inventory/reports/lot-stock' },
            { label: 'Expiry Aging', path: '/inventory/reports/expiry-aging' },
          ] : []),
        ],
      },
    ],
  },
  ...(agriExportEnabled
    ? [{
      label: 'Agri Export',
      icon: HiOutlineTruck,
      permission: 'nav.agri',
      children: [
        { label: 'Intake Notes', path: '/agri/intake', permission: 'agri.intake.view' },
        { label: 'Growers & Farms', path: '/agri/farms', permission: 'agri.growers.view' },
        { label: 'Certifications', path: '/agri/certifications', permission: 'agri.certifications.view' },
        { label: 'Grower Settlement', path: '/agri/settlement', permission: 'agri.settlement.view' },
        { label: 'Export Consignments', path: '/agri/consignments', permission: 'agri.consignments.view' },
      ],
    }]
    : []),
  ...(manufacturingEnabled
    ? [{
        label: 'Manufacturing',
        icon: HiOutlineCube,
        children: [
          { label: 'Bills of Materials', path: '/manufacturing/boms' },
          { label: 'Work Orders', path: '/manufacturing/work-orders' },
          { label: 'Replenishment', path: '/manufacturing/replenishment' },
            { label: 'Work Centers', path: '/manufacturing/setup/work-centers' },
            { label: 'Quality Setup', path: '/manufacturing/setup/quality' },
            { label: 'Maintenance Setup', path: '/manufacturing/setup/maintenance' },
            { label: 'Traceability', path: '/manufacturing/traceability' },
          {
            label: 'Reports',
            children: [
              { label: 'Work In Progress', path: '/manufacturing/reports/wip' },
              { label: 'Scrap Monitoring', path: '/manufacturing/reports/scrap' },
              { label: 'BOM Costing', path: '/manufacturing/reports/bom-costing' },
              { label: 'Production Cost', path: '/manufacturing/reports/production-cost' },
              { label: 'Material Usage Variance', path: '/manufacturing/reports/material-usage' },
              { label: 'Batch & Serial Traceability', path: '/manufacturing/reports/traceability' },
              { label: 'Quality Control', path: '/manufacturing/reports/quality-checks' },
            ],
          },
        ],
      }]
    : []),
  // ── Finance / Back-office ─────────────────────────────────────
  {
    label: 'Finance',
    icon: HiOutlineDocumentText,
    children: [
      { label: 'Dashboard', path: '/finance/dashboard' },
      { label: 'Chart of Accounts', path: '/finance/accounts' },
      { label: 'Journal Entries', path: '/finance/journals' },
      { label: 'Month-End Close', path: '/finance/month-end-close' },
      { label: 'Tax Setup', path: '/finance/tax-codes' },
      { label: 'Withholding Types', path: '/finance/withholding-types' },
      { label: 'Transaction Types', path: '/finance/transaction-types' },
        { label: 'Recurring Journal Templates', path: '/finance/recurring-templates' },
        { label: 'Accruals & Deferrals', path: '/finance/accruals' },
      {
        label: 'Reports',
        children: [
          { label: 'Account Transactions', path: '/finance/reports/account-transactions' },
          { label: 'Income Statement', path: '/finance/reports/income-statement' },
          { label: 'Balance Sheet', path: '/finance/reports/balance-sheet' },
          { label: 'Cash Flow Statement', path: '/finance/reports/cash-flow' },
          { label: 'Trial Balance', path: '/finance/reports/trial-balance' },
          { label: 'Dimension Analysis', path: '/finance/reports/dimension-analysis' },

          { label: 'Tax Report', path: '/finance/reports/tax-report' },
          { label: 'Withholding Register', path: '/finance/reports/withholding-register' },
          { label: 'Tax Returns', path: '/finance/reports/kra-monthly' },
          { label: 'VAT Reconciliation', path: '/finance/vat-reconciliation' },
          { label: 'Cash Position', path: '/finance/reports/cash-position' },
          { label: 'FX Revaluation', path: '/finance/reports/fx-revaluation' },
          { label: 'GL Account Reconciliation', path: '/finance/reports/balance-reconciliation' },
        ],
      },
    ],
  },
  ...(creditControlEnabled ? [{
    label: 'Credit Control',
    icon: HiOutlineShieldCheck,
    children: [
      { label: 'Dashboard', path: '/credit-control' },
      { label: 'Onboarding', path: '/credit-control/onboarding' },
      { label: 'Reminders', path: '/credit-control/reminders' },
      { label: 'Customer Policies', path: '/credit-control/policies' },
      { label: 'Policy Setup', path: '/credit-control/policy-setup' },
    ],
  }] : []),
  {
    label: 'Banking',
    icon: HiOutlineDocumentText,
    children: [
      { label: 'Overview',            path: '/finance/banking' },
      { label: 'Bank Accounts',       path: '/finance/banking/accounts' },
      { label: 'Incoming Payments',   path: '/finance/banking/incoming-payments' },
      { label: 'Outgoing Payments',   path: '/finance/banking/outgoing-payments' },
      { label: 'Bank Transfers',      path: '/finance/banking/transfers' },
      { label: 'Cash Position',       path: '/finance/banking/cash-position' },
      { label: 'Bank Reconciliation', path: '/finance/reconciliation' },
    ],
  },
  // ── Management & Tools ────────────────────────────────────────
  ...(projectManagementEnabled
    ? [{
        label: 'Project',
        icon: HiOutlineClipboardList,
        children: [
          { label: 'Projects', path: '/projects' },
          { label: 'Resources', path: '/projects/resources' },
          {
            label: 'Reports',
            children: [
              { label: 'Project Status', path: '/projects/reports/status' },
              { label: 'Budget vs Billing', path: '/projects/reports/budget-billing' },
              { label: 'Task Progress', path: '/projects/reports/tasks' },
              { label: 'Milestone Tracking', path: '/projects/reports/milestones' },
              { label: 'Resource Allocation', path: '/projects/reports/resources' },
              { label: 'Planned vs Actual', path: '/projects/reports/planned-vs-actual' },
            ],
          },
        ],
      }]
    : []),
  ...(qaiReportGeneratorEnabled
    ? [{
        label: 'Q AI Report Generator',
        path: '/qai-reports',
        icon: HiOutlineSparkles,
      }]
    : []),
  ...(qaiDashboardGeneratorEnabled
    ? [{
        label: 'Q AI Dashboard Generator',
        path: '/qai-dashboards',
        icon: HiOutlineSparkles,
      }]
    : []),
  {
    label: 'Settings',
    icon: HiOutlineCog,
    children: [
      { label: 'General', path: '/settings/general' },
      { label: 'Users Roles & Management', path: '/settings/users-roles' },
      { label: 'Finance Defaults', path: '/settings/finance-defaults' },
      { label: 'Customers / Suppliers Defaults', path: '/settings/customer-supplier-defaults' },
      { label: 'Inventory Defaults', path: '/settings/inventory-defaults' },
      { label: 'License & Feature Flags', path: '/settings/feature-flags' },
      { label: 'Master Data - Import', path: '/settings/master-data-import', permission: 'settings.master_data_import.manage' },
      { label: 'User Defined Fields', path: '/settings/user-defined-fields' },
      { label: 'Report Customization', path: '/settings/report-layouts' },
      { label: 'Audit Log', path: '/settings/audit-log' },
    ],
  },
];

const flattenPages = (items, parent = null) => {
  return items.flatMap((item) => {
    if (item.children && item.children.length > 0) {
      return flattenPages(item.children, item.label);
    }
    if (!item.path) return [];
    return [{ label: item.label, path: item.path, parent }];
  });
};

const isItemActive = (item, pathname) => {
  if (item.path && pathname === item.path) return true;
  if (!item.children) return false;
  return item.children.some((child) => isItemActive(child, pathname));
};

const SidebarSearch = ({ isCollapsed, menuItems }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const allPages = useMemo(() => flattenPages(menuItems), [menuItems]);

  const results = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return allPages
      .map((p, idx) => {
        const label = p.label.toLowerCase();
        const parent = (p.parent || '').toLowerCase();
        const labelStarts = label.startsWith(q);
        const labelHas = label.includes(q);
        const parentHas = parent.includes(q);
        if (!labelHas && !parentHas) return null;
        return {
          ...p,
          _idx: idx,
          _score: labelStarts ? 0 : (labelHas ? 1 : 2),
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a._score !== b._score) return a._score - b._score;
        return a._idx - b._idx;
      })
      .map(({ _idx, _score, ...p }) => p);
  }, [allPages, search]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false); setSearch('');
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (path) => {
    navigate(path);
    setOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setSearch(''); }
  };

  if (isCollapsed) {
    return (
      <div className="px-3 py-2">
        <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-center p-2 rounded-lg text-gray-900 hover:bg-slate-300 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/10 transition">
          <HiOutlineSearch className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="px-3 py-2 relative">
      {!open ? (
        <button onClick={() => setOpen(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-gray-900 hover:bg-slate-300 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/10 transition text-sm">
          <HiOutlineSearch className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs">Search modules...</span>
        </button>
      ) : (
        <div>
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-300 dark:bg-white/10 rounded-lg">
            <HiOutlineSearch className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Search modules..." className="bg-transparent text-gray-900 dark:text-white text-sm w-full outline-none placeholder-gray-400 dark:placeholder-gray-500" />
          </div>
          {results.length > 0 && (
            <div className="absolute left-3 right-3 mt-1 bg-slate-200 border border-slate-300 dark:bg-navy-700 dark:border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto z-50">
              {results.map(r => (
                <button key={r.path} onClick={() => handleSelect(r.path)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-200 dark:hover:bg-white/10 transition flex flex-col">
                  <span className="text-gray-900 dark:text-white font-medium">{r.label}</span>
                  {r.parent && <span className="text-gray-500 text-xs">{r.parent}</span>}
                </button>
              ))}
            </div>
          )}
          {search.trim() && results.length === 0 && (
            <div className="absolute left-3 right-3 mt-1 bg-slate-200 border border-slate-300 dark:bg-navy-700 dark:border-white/10 rounded-lg shadow-xl p-3 z-50">
              <span className="text-gray-500 text-xs">No matches found</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SidebarItem = ({ item, isCollapsed, onExpand, level = 0 }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isExpressMarketItem = item.label === 'Express Market';

  // Auto-open if any child is active
  const isChildActive = hasChildren && item.children.some((c) => isItemActive(c, location.pathname));
  const open = isOpen || isChildActive;

  const containerClass =
    level === 0
      ? 'ml-6 mt-1 space-y-0.5 border-l border-slate-300 dark:border-white/10 pl-3'
      : 'ml-4 mt-1 space-y-0.5 border-l border-slate-300 dark:border-white/10 pl-3';

  const childTextClass = level === 0 ? 'text-xs' : 'text-[11px]';

  const buttonClass =
    level === 0
      ? `w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
          isChildActive
            ? 'text-slate-800 font-semibold dark:text-emerald-400 dark:font-medium'
            : 'text-gray-900 hover:bg-slate-300 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white'
        }`
      : `w-full flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors duration-200 ${
          isChildActive
            ? 'text-slate-800 font-semibold bg-slate-300 dark:text-emerald-400 dark:font-medium dark:bg-emerald-500/10'
            : 'text-gray-800 hover:bg-slate-300 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5'
        }`;

  if (!hasChildren) {
    return (
      <NavLink
        to={item.path}
        end
        onClick={() => {
          if (isCollapsed && level === 0 && onExpand) {
            onExpand();
          }
        }}
        className={({ isActive }) => {
          const base = 'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200';
          if (isActive) {
            return `${base} bg-slate-600 text-white dark:bg-emerald-500/20 dark:text-emerald-400`;
          }
          if (isExpressMarketItem) {
            return `${base} text-emerald-700 hover:text-emerald-800 hover:bg-slate-300 dark:text-amber-400/65 dark:hover:text-amber-300 dark:hover:bg-white/5`;
          }
          return `${base} text-gray-900 hover:bg-slate-300 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white`;
        }}
      >
        {item.icon && <item.icon className="w-5 h-5 flex-shrink-0" />}
        {!isCollapsed && (
          <>
            <span className="flex-1">{item.label}</span>
            {item.badge > 0 && (
              <span className="ml-auto bg-red-500 text-white text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full">
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  }

  return (
    <div>
      <button
        onClick={() => {
          if (isCollapsed && level === 0 && onExpand) {
            setIsOpen(true);
            onExpand();
            return;
          }
          setIsOpen(!open);
        }}
        className={buttonClass}
      >
        {item.icon && level === 0 && <item.icon className="w-5 h-5 flex-shrink-0" />}
        {!isCollapsed && (
          <>
            <span className="flex-1 text-left">{item.label}</span>
            {open ? (
              <HiOutlineChevronDown className="w-4 h-4" />
            ) : (
              <HiOutlineChevronRight className="w-4 h-4" />
            )}
          </>
        )}
      </button>
      {!isCollapsed && open && (
        <div className={containerClass}>
          {item.children.map((child) => (
            child.children && child.children.length > 0 ? (
              <SidebarItem key={child.label} item={child} isCollapsed={isCollapsed} onExpand={onExpand} level={level + 1} />
            ) : (
              <NavLink
                key={child.path}
                to={child.path}
                end
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-1.5 rounded ${childTextClass} font-medium transition-colors duration-200 ${
                    isActive
                      ? 'text-white bg-slate-600 dark:text-emerald-400 dark:bg-emerald-500/10'
                      : 'text-gray-800 hover:bg-slate-300 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5'
                  }`
                }
              >
                {child.icon && <child.icon className="w-3.5 h-3.5 flex-shrink-0" />}
                <span className="flex-1">{child.label}</span>
                {child.badge > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full">
                    {child.badge > 99 ? '99+' : child.badge}
                  </span>
                )}
              </NavLink>
            )
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ isCollapsed = false, onToggleCollapse, isMobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const { can, isNavHidden, isAiEnabled } = useAuth();
  const [featureFlags, setFeatureFlags] = useState({
    agriExportEnabled: false,
    batchTrackingEnabled: false,
    itemVariantsEnabled: false,
    projectManagementEnabled: false,
    manufacturingEnabled: false,
    qaiReportGeneratorEnabled: false,
    qaiDashboardGeneratorEnabled: false,
    deliveryManagementEnabled: false,
    crmEnabled: false,
    crmMailboxEnabled: false,
    posEnabled: false,
    expressMarketEnabled: false,
    creditControlEnabled: false,
    procurementEnabled: false,
  });
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [todoCount, setTodoCount] = useState(0);
  const mailboxEnabledRef = useRef(false);

  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        const res = await inventoryService.getFeatureFlags();
        const flags = res.data || [];
        const mailboxEnabled = !!flags.find((flag) => flag.feature_key === 'crm_leads')?.is_enabled;
        setFeatureFlags({
          agriExportEnabled: !!flags.find((flag) => flag.feature_key === 'agri_export')?.is_enabled,
          batchTrackingEnabled: !!flags.find((flag) => flag.feature_key === 'batch_tracking')?.is_enabled,
          itemVariantsEnabled: !!flags.find((flag) => flag.feature_key === 'item_variants')?.is_enabled,
          projectManagementEnabled: !!flags.find((flag) => flag.feature_key === 'project_management')?.is_enabled,
          manufacturingEnabled: !!flags.find((flag) => flag.feature_key === 'manufacturing')?.is_enabled,
          qaiReportGeneratorEnabled: !!flags.find((flag) => flag.feature_key === 'q_ai_report_generator')?.is_enabled,
          qaiDashboardGeneratorEnabled: !!flags.find((flag) => flag.feature_key === 'q_ai_dashboard_generator')?.is_enabled,
          deliveryManagementEnabled: !!flags.find((flag) => flag.feature_key === 'delivery_management')?.is_enabled,
          crmEnabled: mailboxEnabled,
          crmMailboxEnabled: mailboxEnabled,
          posEnabled: !!flags.find((flag) => flag.feature_key === 'pos')?.is_enabled,
          expressMarketEnabled: !!flags.find((flag) => flag.feature_key === 'express_market')?.is_enabled,
          creditControlEnabled: !!flags.find((flag) => flag.feature_key === 'credit_control')?.is_enabled,
          procurementEnabled: !!flags.find((flag) => flag.feature_key === 'procurement')?.is_enabled,
        });
        mailboxEnabledRef.current = mailboxEnabled;
      } catch {
        setFeatureFlags({ batchTrackingEnabled: false, itemVariantsEnabled: false, projectManagementEnabled: false, manufacturingEnabled: false, qaiReportGeneratorEnabled: false, qaiDashboardGeneratorEnabled: false, deliveryManagementEnabled: false, crmEnabled: false, crmMailboxEnabled: false, posEnabled: false, expressMarketEnabled: false, creditControlEnabled: false, procurementEnabled: false });
        mailboxEnabledRef.current = false;
      }
    };

    loadFeatureFlags();
  }, [location.pathname]);

  // Immediate fetch when mailbox first becomes available
  useEffect(() => {
    if (!featureFlags.crmMailboxEnabled) return;
    mailboxService.getUnreadCount()
      .then(res => setInboxUnreadCount(res.data?.unread || 0))
      .catch(() => {});
  }, [featureFlags.crmMailboxEnabled]);

  // Background poll every 60s — set up once on mount, uses ref to avoid stale closure
  useEffect(() => {
    const interval = setInterval(() => {
      if (!mailboxEnabledRef.current) return;
      mailboxService.getUnreadCount()
        .then(res => setInboxUnreadCount(res.data?.unread || 0))
        .catch(() => {});
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Fetch active task count on mount and poll every 120s
  useEffect(() => {
    const fetchTodoCount = () => {
      taskService.getTasks()
        .then(res => {
          const tasks = Array.isArray(res.data) ? res.data : [];
          const active = tasks.filter(t => t.status !== 'completed').length;
          setTodoCount(active);
        })
        .catch(() => {});
    };
    fetchTodoCount();
    const interval = setInterval(fetchTodoCount, 120_000);
    // Refresh immediately when a task is created/updated/deleted elsewhere
    window.addEventListener('tasks:changed', fetchTodoCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('tasks:changed', fetchTodoCount);
    };
  }, []);

  const menuItems = useMemo(() => {
    const flags = isAiEnabled ? featureFlags : { ...featureFlags, qaiReportGeneratorEnabled: false, qaiDashboardGeneratorEnabled: false };
    const baseItems = buildMenuItems({ ...flags, inboxUnreadCount, todoCount });
    return filterMenuItemsByPermission(baseItems, can, isNavHidden);
  }, [featureFlags, can, isNavHidden, isAiEnabled, inboxUnreadCount, todoCount]);

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-slate-200 border-r border-slate-300 dark:bg-navy-800 dark:border-white/10 flex flex-col z-40 transition-all duration-300 w-64 ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 ${isCollapsed ? 'lg:w-16' : 'lg:w-64'}`}
    >
      {/* Logo */}
      <div className="flex items-center justify-center px-3 h-16 bg-navy-800 border-b border-white/10 flex-shrink-0">
        {isCollapsed ? (
          <img src={logoImg} alt="Journey.AI" className="h-8 w-auto object-contain" />
        ) : (
           <img src={logoImg} alt="Journey.AI" style={{ height: '46px', width: 'auto', maxWidth: '100%', transform: 'scale(1.4)', transformOrigin: 'center' }} className="object-contain" />
        )}
      </div>

      {/* Search */}
      <SidebarSearch isCollapsed={isCollapsed} menuItems={menuItems} />

      {/* Navigation */}
      <nav
        className="flex-1 overflow-y-auto py-4 px-3 space-y-1"
        onClick={(e) => {
          // Close the mobile drawer when a navigation link is followed
          // (but not when tapping an expand/collapse group toggle).
          if (e.target.closest('a')) onMobileClose?.();
        }}
      >
        {menuItems.map((item) => (
          <SidebarItem key={item.label} item={item} isCollapsed={isCollapsed} onExpand={() => isCollapsed && onToggleCollapse?.()} />
        ))}
      </nav>

      {/* Collapse toggle — desktop only; on mobile the drawer closes via backdrop/nav */}
      <button
        onClick={onToggleCollapse}
        className="hidden lg:block p-3 text-gray-900 hover:text-black border-t border-slate-300 dark:text-gray-400 dark:hover:text-white dark:border-white/10 text-xs"
      >
        {isCollapsed ? '→' : '← Collapse'}
      </button>
    </aside>
  );
};

export default Sidebar;
